import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home as HomeIcon, Send, SkipForward, Pencil, Check, Loader2, MessageCircleHeart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, API, fmtErr, fmtRent, bedsLabel } from "../lib/api";
import SEO from "../components/SEO";
import ListingCard from "../components/ListingCard";

const SKEY = "tendly-chat-v1";

const STEPS = [
  { id: "locations", q: "Wonderful. First — where would you love to live? Pick one or more areas, or type your own.", type: "locations" },
  { id: "radius_km", q: "Great choice. How far around there are you open to?", type: "radius" },
  { id: "beds", q: "How many bedrooms do you need? You can pick more than one.", type: "multi", options: [["studio", "Studio"], ["1", "1"], ["2", "2"], ["3", "3"], ["4+", "4+"]] },
  { id: "baths", q: "And bathrooms?", type: "multi", options: [["1", "1"], ["1.5", "1.5"], ["2", "2"], ["3+", "3+"]] },
  { id: "budget", q: "What's your monthly budget range? Drag the handles.", type: "budget" },
  { id: "furnished", q: "Furnished or unfurnished?", type: "single", options: [["furnished", "Furnished"], ["unfurnished", "Unfurnished"], ["no_preference", "No preference"]] },
  { id: "parking", q: "Do you need parking?", type: "single", options: [["need", "Need parking"], ["no_need", "Don't need parking"], ["no_preference", "No preference"]] },
  { id: "accessibility", q: "Any accessibility needs? Pick all that apply.", type: "multi", options: [["step-free", "Step-free access"], ["elevator", "Elevator"], ["roll-in shower", "Roll-in shower"], ["none", "None"]], extra: "accessibility_detail" },
  { id: "pets", q: "Any pets coming with you?", type: "multi", options: [["none", "No pets"], ["cat", "Cat"], ["dog", "Dog"], ["other", "Other"]] },
  { id: "lease_duration", q: "What lease length works for you?", type: "single", options: [["6", "6 months"], ["12", "12 months"], ["month_to_month", "Month-to-month"], ["flexible", "Flexible"]] },
  { id: "move_in_date", q: "When would you like to move in?", type: "date" },
  { id: "move_out_date", q: "And a move-out date? Totally fine if you're not sure.", type: "dateOpt" },
  { id: "occupants", q: "How many people will live there? (optional)", type: "numOpt" },
  { id: "note", q: "Almost done. Anything else about your ideal home? A view, quiet street, near transit — tell me in your own words.", type: "note" },
  { id: "contact", q: "Last step — where should our agent reach you?", type: "contact" },
];

const SLOTS = ["morning", "afternoon", "evening"];

function bubbleIn(delay = 0) {
  return { initial: { opacity: 0, scale: 0.95, y: 10 }, animate: { opacity: 1, scale: 1, y: 0 }, transition: { type: "spring", stiffness: 300, damping: 25, delay } };
}

export default function Chat() {
  const [state, setState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SKEY)) || null; } catch { return null; }
  });
  const [hoods, setHoods] = useState([]);
  const [freeLoc, setFreeLoc] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "", consent: false });
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const sessionRef = useRef(sessionStorage.getItem("tendly-session"));

  useEffect(() => {
    api.get("/neighborhoods").then((r) => setHoods(r.data)).catch(() => {});
    if (!sessionRef.current) {
      api.post("/chat/session").then((r) => {
        sessionRef.current = r.data.session_id;
        sessionStorage.setItem("tendly-session", r.data.session_id);
      }).catch(() => {});
    }
    if (!state) {
      setState({
        step: 0, phase: "questions", criteria: {}, selected: [], availability: [],
        messages: [
          { role: "bot", text: "Hi, I'm Tendly — your rental concierge. I help people find their exact home in Vancouver, completely free." },
          { role: "bot", text: STEPS[0].q },
        ],
        results: null, leadId: null, noteSignals: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state) sessionStorage.setItem(SKEY, JSON.stringify(state));
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [state]);

  if (!state) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const patchSession = (criteria, step, completed = false) => {
    if (!sessionRef.current) return;
    api.patch(`/chat/session/${sessionRef.current}`, { criteria, step, ...(completed ? { completed: true } : {}) }).catch(() => {});
  };

  const current = STEPS[state.step];
  const totalQ = STEPS.length;

  const push = (msgs, extra = {}) => setState((s) => ({ ...s, messages: [...s.messages, ...msgs], ...extra }));

  const answer = (value, label) => {
    const criteria = { ...state.criteria, [current.id]: value };
    answerRaw(criteria, label);
  };

  const answerRaw = (criteria, label) => {
    const nextStep = state.step + 1;
    const msgs = [{ role: "user", text: label }];
    if (nextStep < totalQ) msgs.push({ role: "bot", text: STEPS[nextStep].q });
    else msgs.push({ role: "bot", text: "Here's your search summary. Anything you'd like to tweak before I find your homes?" });
    push(msgs, { criteria, step: Math.min(nextStep, totalQ - 1), phase: nextStep < totalQ ? "questions" : "confirm" });
    patchSession(criteria, nextStep);
  };

  const toggleMulti = (val, label) => {
    const cur = state.criteria[current.id] || [];
    let next;
    if (val === "none") next = cur.includes("none") ? [] : ["none"];
    else next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur.filter((v) => v !== "none"), val];
    setState((s) => ({ ...s, criteria: { ...s.criteria, [current.id]: next } }));
  };

  const confirmMulti = () => {
    const vals = state.criteria[current.id] || [];
    if (!vals.length) return toast.error("Pick at least one option, or tap an option above.");
    const labels = current.options.filter(([v]) => vals.includes(v)).map(([, l]) => l).join(", ");
    answer(vals, labels);
  };

  const submitNote = async (note) => {
    const criteria = { ...state.criteria, note };
    const msgs = [{ role: "user", text: note || "That's everything!" }];
    setBusy(true);
    let signals = state.noteSignals;
    if (note) {
      try {
        const r = await api.post("/chat/parse-note", { note });
        signals = r.data.signals || {};
        msgs.push({ role: "bot", text: r.data.ack });
      } catch { /* scripted fallback already covered */ }
    }
    setBusy(false);
    msgs.push({ role: "bot", text: "Last step — where should our agent reach you?" });
    push(msgs, { criteria, noteSignals: signals, step: totalQ - 1, phase: "questions" });
    setState((s) => ({ ...s, step: totalQ - 1 }));
    patchSession(criteria, totalQ - 1);
  };

  const submitContact = () => {
    const { first_name, last_name, email, phone, consent } = contact;
    if (!first_name.trim() || !last_name.trim()) return toast.error("Please add your first and last name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Please enter a valid email address.");
    if (!/^[+\d][\d\s\-()]{6,}$/.test(phone)) return toast.error("Please enter a valid phone number.");
    if (!consent) return toast.error("We need your consent to contact you about your search (PIPEDA).");
    const criteria = { ...state.criteria, contact };
    push([{ role: "user", text: `${first_name} ${last_name} · ${email}` },
          { role: "bot", text: "Here's your search summary. Anything you'd like to tweak before I find your homes?" }],
      { criteria, phase: "confirm" });
    patchSession(criteria, totalQ, true);
  };

  const runMatch = async () => {
    const bm = state.criteria.budget_min ?? 1000, bx = state.criteria.budget_max ?? 4000;
    if (!(bx > 0) || bx < bm) { toast.error("Please set a valid budget range first."); return; }
    const criteriaOut = { ...state.criteria, budget_min: bm, budget_max: bx };
    setBusy(true);
    push([{ role: "user", text: "Find my homes" }, { role: "bot", text: "Searching real listings across Vancouver for you… one moment." }]);
    try {
      const c = state.criteria.contact;
      const [leadRes, matchRes] = await Promise.all([
        api.post("/leads", {
          session_id: sessionRef.current, first_name: c.first_name, last_name: c.last_name,
          email: c.email, phone: c.phone, consent: c.consent, criteria: criteriaOut,
          transcript: state.messages, note_signals: state.noteSignals,
        }),
        api.post("/chat/match", criteriaOut),
      ]);
      setState((s) => ({ ...s, criteria: criteriaOut, leadId: leadRes.data.lead_id, results: matchRes.data, phase: "results",
        messages: [...s.messages, { role: "bot", text: matchRes.data.perfect.length
          ? `I found ${matchRes.data.perfect.length} home${matchRes.data.perfect.length > 1 ? "s" : ""} that match everything you want. Select the ones you'd like to tour, or explore similar matches below.`
          : matchRes.data.similar.length
          ? "No single home ticks every box right now — but these close matches are worth a look. Select any you'd like to tour."
          : "I don't have a live home matching that right now — new listings arrive as landlords list with us. I've saved your search and an agent will reach out within 24–48 hours with options." }] }));
    } catch (e) {
      toast.error(fmtErr(e));
      setState((s) => ({ ...s, messages: s.messages.slice(0, -1) }));
    }
    setBusy(false);
  };

  const submitViewings = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${state.leadId}/viewings`, {
        listing_ids: state.selected,
        availability: state.availability.map((k) => { const [date, slot] = k.split("|"); return { date, slot }; }),
      });
      setState((s) => ({ ...s, phase: "done",
        messages: [...s.messages, { role: "bot", text: "You're all set! One of our agents will reach out within 24 to 48 hours to line up your viewings. A confirmation email is on its way to you." }] }));
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const editStep = (i) => setState((s) => ({ ...s, phase: "questions", step: i,
    messages: [...s.messages, { role: "bot", text: `No problem — let's update that. ${STEPS[i].q}` }] }));

  const restart = () => { sessionStorage.removeItem(SKEY); sessionStorage.removeItem("tendly-session"); window.location.reload(); };

  return (
    <div className="px-4 sm:px-6 py-10 md:py-16">
      <SEO title="Live chat — Tendly AI rental concierge" description="Chat with Tendly to find your exact rental home in Vancouver. Free for renters." />
      <div className="max-w-2xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="relative w-11 h-11 rounded-full bg-primary flex items-center justify-center">
              <HomeIcon className="w-5 h-5 text-primary-foreground" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-background" aria-label="online" />
            </span>
            <div>
              <p className="font-heading font-semibold">Tendly concierge</p>
              <p className="text-xs text-muted-foreground">Online · replies instantly</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {state.phase === "questions" && (
              <span data-testid="chat-progress" className="text-xs font-medium text-muted-foreground">Question {state.step + 1} of {totalQ}</span>
            )}
            <button data-testid="chat-restart" onClick={restart} className="text-xs text-muted-foreground hover:text-foreground underline">Start over</button>
          </div>
        </div>
        {state.phase === "questions" && (
          <div className="h-1.5 rounded-full bg-secondary mb-6 overflow-hidden" role="progressbar" aria-valuenow={state.step + 1} aria-valuemax={totalQ}>
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${((state.step + 1) / totalQ) * 100}%` }} />
          </div>
        )}

        {/* Messages */}
        <div ref={listRef} role="log" className="space-y-4 mb-6 max-h-[60vh] md:max-h-[52vh] overflow-y-auto overscroll-contain pr-1" aria-live="polite">
          <AnimatePresence initial={false}>
            {state.messages.map((m, i) => (
              <motion.div key={i} {...bubbleIn()} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md shadow-sm"}`}>
                  {m.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="rounded-2xl border border-border bg-card shadow-lg p-5" data-testid="chat-input-area">
          {state.phase === "questions" && current && (
            <StepWidget step={current} state={state} setState={setState} hoods={hoods}
              freeLoc={freeLoc} setFreeLoc={setFreeLoc} answer={answer} answerRaw={answerRaw} toggleMulti={toggleMulti}
              confirmMulti={confirmMulti} submitNote={submitNote} contact={contact} setContact={setContact}
              submitContact={submitContact} busy={busy}
              goBack={() => state.step > 0 && setState((s) => ({ ...s, step: s.step - 1, messages: [...s.messages, { role: "bot", text: STEPS[s.step - 1].q }] }))} />
          )}

          {state.phase === "confirm" && <Confirm criteria={state.criteria} editStep={editStep} runMatch={runMatch} busy={busy} />}

          {state.phase === "results" && state.results && (
            <Results state={state} setState={setState} />
          )}

          {state.phase === "availability" && (
            <Availability state={state} setState={setState} />
          )}

          {state.phase === "review" && (
            <Review state={state} submitViewings={submitViewings} busy={busy} back={() => setState((s) => ({ ...s, phase: "availability" }))} />
          )}

          {state.phase === "done" && (
            <div data-testid="chat-done" className="text-center py-6">
              <span className="inline-flex w-14 h-14 rounded-full bg-teal-50 dark:bg-teal-900/40 text-primary items-center justify-center"><Check className="w-7 h-7" /></span>
              <h3 className="mt-4 font-heading font-bold text-2xl">You're all set</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">An agent will reach out within 24–48 hours to confirm your viewings. We've emailed you a summary.</p>
              <a href="/listings" className="mt-6 inline-block rounded-full border border-border px-6 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors">Keep browsing homes</a>
            </div>
          )}
        </div>

        <AskTendly />
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} aria-pressed={active}
      className={`rounded-full min-h-[44px] px-4 py-2.5 text-sm font-medium border transition-all active:scale-95 ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary bg-background"}`}>
      {children}
    </button>
  );
}

function StepWidget({ step, state, setState, hoods, freeLoc, setFreeLoc, answer, answerRaw, toggleMulti, confirmMulti, submitNote, contact, setContact, submitContact, busy, goBack }) {
  const c = state.criteria;
  const [note, setNote] = useState(c.note || "");
  const [extra, setExtra] = useState("");

  return (
    <div>
      {state.step > 0 && (
        <button data-testid="chat-back" onClick={goBack} className="mb-3 text-xs text-muted-foreground hover:text-foreground underline">← Back to previous question</button>
      )}

      {step.type === "locations" && (
        <div>
          <div className="flex flex-wrap gap-2">
            {hoods.map((h) => (
              <Chip key={h.slug} tid={`chat-option-chip-${h.slug}`} active={(c.locations || []).includes(h.name)}
                onClick={() => {
                  const cur = c.locations || [];
                  const next = cur.includes(h.name) ? cur.filter((x) => x !== h.name) : [...cur, h.name];
                  setState((s) => ({ ...s, criteria: { ...s.criteria, locations: next } }));
                }}>{h.name}</Chip>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="chat-location-input" value={freeLoc} onChange={(e) => setFreeLoc(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && freeLoc.trim()) { const cur = c.locations || []; if (!cur.includes(freeLoc.trim())) setState((s) => ({ ...s, criteria: { ...s.criteria, locations: [...cur, freeLoc.trim()] } })); setFreeLoc(""); } }}
              placeholder="Or type an area or address…" aria-label="Type a neighbourhood or address"
              className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm" />
            <button data-testid="chat-location-add" onClick={() => { if (freeLoc.trim()) { const cur = c.locations || []; if (!cur.includes(freeLoc.trim())) setState((s) => ({ ...s, criteria: { ...s.criteria, locations: [...cur, freeLoc.trim()] } })); setFreeLoc(""); } }}
              className="rounded-full border border-border px-4 text-sm font-medium">Add</button>
          </div>
          <button data-testid="chat-locations-continue" disabled={!(c.locations || []).length}
            onClick={() => answer(c.locations, (c.locations || []).join(", "))}
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground py-2.5 min-h-[44px] text-sm font-semibold disabled:opacity-40">Continue</button>
        </div>
      )}

      {step.type === "radius" && (
        <div>
          <input data-testid="chat-radius-slider" type="range" min="0.5" max="15" step="0.5"
            value={c.radius_km ?? 3} onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, radius_km: Number(e.target.value) } }))}
            className="w-full accent-[#0D5C63]" aria-label="Search radius in kilometres" />
          <p className="text-center font-heading font-bold text-2xl mt-2">{c.radius_km ?? 3} km</p>
          <button data-testid="chat-radius-continue" onClick={() => answer(c.radius_km ?? 3, `Within ${c.radius_km ?? 3} km`)}
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground py-2.5 min-h-[44px] text-sm font-semibold">Continue</button>
        </div>
      )}

      {step.type === "multi" && (
        <div>
          <div className="flex flex-wrap gap-2">
            {step.options.map(([v, l]) => (
              <Chip key={v} tid={`chat-option-${step.id}-${v.replace(/[.+]/g, "")}`} active={(c[step.id] || []).includes(v)} onClick={() => toggleMulti(v, l)}>{l}</Chip>
            ))}
          </div>
          {step.extra && (
            <input data-testid="chat-accessibility-detail" value={extra} onChange={(e) => setExtra(e.target.value)}
              placeholder="Other needs? (optional)" aria-label="Other accessibility needs"
              className="mt-3 w-full rounded-full border border-input bg-background px-4 py-2 text-sm"
              onBlur={() => extra && setState((s) => ({ ...s, criteria: { ...s.criteria, [step.extra]: extra } }))} />
          )}
          <button data-testid={`chat-${step.id}-continue`} onClick={confirmMulti}
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground py-2.5 min-h-[44px] text-sm font-semibold">Continue</button>
        </div>
      )}

      {step.type === "single" && (
        <div className="flex flex-wrap gap-2">
          {step.options.map(([v, l]) => (
            <Chip key={v} tid={`chat-option-${step.id}-${v.replace(/_/g, "-")}`} active={c[step.id] === v} onClick={() => answer(v, l)}>{l}</Chip>
          ))}
        </div>
      )}

      {step.type === "budget" && (
        <div>
          {(() => {
            const lo = c.budget_min ?? 1000, hi = c.budget_max ?? 4000;
            return (
              <div>
                <div className="flex justify-between text-sm font-semibold mb-2">
                  <span data-testid="budget-min-label">${lo.toLocaleString()}</span>
                  <span data-testid="budget-max-label">${hi >= 6000 ? "6,000+" : hi.toLocaleString()}</span>
                </div>
                <label className="block text-xs text-muted-foreground mb-1">Minimum</label>
                <input data-testid="chat-budget-min" type="range" min="800" max="6000" step="50" value={lo}
                  onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, budget_min: Math.min(Number(e.target.value), hi - 50) } }))}
                  className="w-full accent-[#0D5C63]" aria-label="Minimum monthly rent" />
                <label className="block text-xs text-muted-foreground mb-1 mt-3">Maximum</label>
                <input data-testid="chat-budget-max" type="range" min="850" max="6000" step="50" value={hi}
                  onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, budget_max: Math.max(Number(e.target.value), lo + 50) } }))}
                  className="w-full accent-[#FF6B5B]" aria-label="Maximum monthly rent" />
                <button data-testid="chat-budget-submit" onClick={() => {
                    if (!(hi > 0) || hi < lo) { toast.error("Maximum budget must be at least the minimum."); return; }
                    const criteria = { ...c, budget_min: lo, budget_max: hi };
                    setState((s) => ({ ...s, criteria }));
                    answerRaw(criteria, `$${lo.toLocaleString()} – $${hi >= 6000 ? "6,000+" : hi.toLocaleString()}/mo`);
                  }}
                  className="mt-4 w-full rounded-full bg-primary text-primary-foreground py-2.5 min-h-[44px] text-sm font-semibold">Continue</button>
              </div>
            );
          })()}
        </div>
      )}

      {step.type === "date" && (
        <div>
          <input data-testid="chat-move-in-date" type="date" value={c.move_in_date || ""}
            onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, move_in_date: e.target.value } }))}
            className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" aria-label="Move-in date" />
          <button data-testid="chat-date-continue" disabled={!c.move_in_date}
            onClick={() => answer(c.move_in_date, c.move_in_date)}
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground py-2.5 min-h-[44px] text-sm font-semibold disabled:opacity-40">Continue</button>
        </div>
      )}

      {step.type === "dateOpt" && (
        <div>
          <input data-testid="chat-move-out-date" type="date" value={c.move_out_date || ""}
            onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, move_out_date: e.target.value } }))}
            className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" aria-label="Move-out date (optional)" />
          <div className="mt-3 flex gap-2">
            <button data-testid="chat-moveout-continue" disabled={!c.move_out_date} onClick={() => answer(c.move_out_date, c.move_out_date)}
              className="flex-1 rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold disabled:opacity-40">Continue</button>
            <button data-testid="chat-moveout-skip" onClick={() => answer(null, "Not sure / open-ended")}
              className="flex-1 rounded-full border border-border py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1"><SkipForward className="w-4 h-4" /> Not sure</button>
          </div>
        </div>
      )}

      {step.type === "numOpt" && (
        <div className="flex gap-2">
          <input data-testid="chat-occupants" type="number" min="1" max="10" value={c.occupants || ""}
            onChange={(e) => setState((s) => ({ ...s, criteria: { ...s.criteria, occupants: e.target.value } }))}
            placeholder="e.g. 2" aria-label="Number of occupants"
            className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm" />
          <button data-testid="chat-occupants-continue" onClick={() => answer(c.occupants || null, c.occupants ? `${c.occupants} ${Number(c.occupants) > 1 ? "people" : "person"}` : "Skip")}
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">{c.occupants ? "Continue" : "Skip"}</button>
        </div>
      )}

      {step.type === "note" && (
        <div>
          <textarea data-testid="chat-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
            placeholder="e.g. Bright corner unit, quiet street, close to the Canada Line, in-suite laundry would be amazing…"
            aria-label="Anything else about your ideal home"
            className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm" />
          <div className="mt-3 flex gap-2">
            <button data-testid="chat-note-continue" disabled={busy} onClick={() => submitNote(note.trim())}
              className="flex-1 rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue</button>
            <button data-testid="chat-note-skip" disabled={busy} onClick={() => submitNote("")}
              className="rounded-full border border-border px-5 py-2.5 text-sm font-medium">Skip</button>
          </div>
        </div>
      )}

      {step.type === "contact" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="chat-first-name" value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} autoFocus
              placeholder="First name" aria-label="First name" className="rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
            <input data-testid="chat-last-name" value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })}
              placeholder="Last name" aria-label="Last name" className="rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
          </div>
          <input data-testid="chat-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
            placeholder="Email address" aria-label="Email address" className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
          <input data-testid="chat-phone" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            placeholder="Phone number" aria-label="Phone number" className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
          <label className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed cursor-pointer">
            <input data-testid="chat-consent" type="checkbox" checked={contact.consent}
              onChange={(e) => setContact({ ...contact, consent: e.target.checked })} className="mt-0.5 accent-[#0D5C63]" />
            <span>I agree to Tendly contacting me about my rental search and I've read the <a href="/privacy" target="_blank" className="text-primary underline">Privacy Policy</a>.</span>
          </label>
          <button data-testid="chat-contact-continue" onClick={submitContact}
            className="w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold">Review my search</button>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, onEdit, tid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0">
      <div className="text-sm"><span className="text-muted-foreground">{label}:</span> <span className="font-medium">{value || "—"}</span></div>
      <button data-testid={tid} onClick={onEdit} aria-label={`Edit ${label}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function Confirm({ criteria, editStep, runMatch, busy }) {
  const opt = (id, val) => STEPS.find((s) => s.id === id)?.options?.find(([v]) => v === val)?.[1] || val;
  const multi = (id) => (criteria[id] || []).join(", ");
  return (
    <div data-testid="chat-confirm">
      <SummaryRow label="Locations" value={(criteria.locations || []).join(", ")} onEdit={() => editStep(0)} tid="edit-locations" />
      <SummaryRow label="Radius" value={`${criteria.radius_km ?? 3} km`} onEdit={() => editStep(1)} tid="edit-radius" />
      <SummaryRow label="Bedrooms" value={multi("beds")} onEdit={() => editStep(2)} tid="edit-beds" />
      <SummaryRow label="Bathrooms" value={multi("baths")} onEdit={() => editStep(3)} tid="edit-baths" />
      <SummaryRow label="Budget" value={`$${(criteria.budget_min ?? 1000).toLocaleString()} – $${(criteria.budget_max ?? 4000).toLocaleString()}/mo`} onEdit={() => editStep(4)} tid="edit-budget" />
      <SummaryRow label="Furnished" value={opt("furnished", criteria.furnished)} onEdit={() => editStep(5)} tid="edit-furnished" />
      <SummaryRow label="Parking" value={opt("parking", criteria.parking)} onEdit={() => editStep(6)} tid="edit-parking" />
      <SummaryRow label="Pets" value={multi("pets")} onEdit={() => editStep(8)} tid="edit-pets" />
      <SummaryRow label="Lease" value={opt("lease_duration", criteria.lease_duration)} onEdit={() => editStep(9)} tid="edit-lease" />
      <SummaryRow label="Move-in" value={criteria.move_in_date} onEdit={() => editStep(10)} tid="edit-movein" />
      <button data-testid="chat-find-homes" onClick={runMatch} disabled={busy}
        className="mt-5 w-full rounded-full bg-accent text-accent-foreground py-3 font-semibold hover:bg-coral-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Find my homes
      </button>
    </div>
  );
}

function Results({ state, setState }) {
  const { perfect, similar } = state.results;
  const toggle = (id) => setState((s) => ({ ...s, selected: s.selected.includes(id) ? s.selected.filter((x) => x !== id) : [...s.selected, id] }));
  if (!perfect.length && !similar.length) {
    return (
      <div data-testid="chat-results-empty" className="text-center py-6">
        <h3 className="font-heading font-semibold text-lg">You're on the list</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">An agent will email you within 24–48 hours as soon as a matching home is listed. Your search criteria are saved to your lead.</p>
        <a href="/" className="mt-5 inline-block rounded-full border border-border px-6 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors">Back to Tendly</a>
      </div>
    );
  }
  return (
    <div data-testid="chat-results">
      {perfect.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold text-lg mb-3">Homes that match everything you want</h3>
          <div className="space-y-4">
            {perfect.map((l) => <ListingCard key={l.listing_id} listing={l} match={100} selectable selected={state.selected.includes(l.listing_id)} onToggleSelect={toggle} />)}
          </div>
        </div>
      )}
      {similar.length > 0 && (
        <div className="mt-6">
          <h3 className="font-heading font-semibold text-lg mb-3">Explore more similar homes</h3>
          <div className="space-y-4">
            {similar.slice(0, 8).map((l) => <ListingCard key={l.listing_id} listing={l} match={l.match} selectable selected={state.selected.includes(l.listing_id)} onToggleSelect={toggle} />)}
          </div>
        </div>
      )}
      <button data-testid="chat-continue-availability" disabled={!state.selected.length}
        onClick={() => setState((s) => ({ ...s, phase: "availability", messages: [...s.messages, { role: "user", text: `Selected ${s.selected.length} home${s.selected.length > 1 ? "s" : ""}` }, { role: "bot", text: "Great picks. When works best for you this week? Pick as many slots as you like." }] }))}
        className="mt-6 w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold disabled:opacity-40">
        Continue with {state.selected.length} selected
      </button>
    </div>
  );
}

function Availability({ state, setState }) {
  const days = [...Array(7)].map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1);
    return d;
  });
  const toggle = (key) => setState((s) => ({ ...s, availability: s.availability.includes(key) ? s.availability.filter((x) => x !== key) : [...s.availability, key] }));
  const [activeDay, setActiveDay] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  return (
    <div data-testid="chat-availability">
      <div className="md:hidden">
        <div data-testid="chat-day-ribbon" className="flex overflow-x-auto gap-2 pb-2 snap-x snap-mandatory">
          {days.map((d) => {
            const ds = d.toISOString().slice(0, 10);
            const active = activeDay === ds;
            const picked = state.availability.some((k) => k.startsWith(ds));
            return (
              <button key={ds} data-testid={`chat-day-tab-${ds}`} onClick={() => setActiveDay(ds)} aria-pressed={active}
                className={`snap-start flex-none min-h-[44px] px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors active:scale-95 ${active ? "bg-primary text-primary-foreground border-primary" : picked ? "border-primary/60 text-primary" : "border-border"}`}>
                {d.toLocaleDateString("en-CA", { weekday: "short" })} {d.getDate()}{picked ? " •" : ""}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {SLOTS.map((slot) => {
            const key = `${activeDay}|${slot}`;
            const active = state.availability.includes(key);
            return (
              <button key={key} data-testid={`chat-mslot-${activeDay}-${slot}`} onClick={() => toggle(key)} aria-pressed={active}
                className={`min-h-[44px] px-3 py-2.5 rounded-lg border text-sm font-medium transition-all active:scale-95 ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                {slot}
              </button>
            );
          })}
        </div>
      </div>
      <div className="hidden md:block overflow-x-auto -mx-2 px-2">
        <div className="grid grid-cols-7 gap-2 min-w-[640px]">
          {days.map((d) => {
            const ds = d.toISOString().slice(0, 10);
            return (
              <div key={ds} className="text-center">
                <p className="text-xs font-semibold mb-2">{d.toLocaleDateString("en-CA", { weekday: "short" })}<br /><span className="text-muted-foreground font-normal">{d.getDate()}</span></p>
                <div className="space-y-1.5">
                  {SLOTS.map((slot) => {
                    const key = `${ds}|${slot}`;
                    const active = state.availability.includes(key);
                    return (
                      <button key={key} data-testid={`slot-${ds}-${slot}`} onClick={() => toggle(key)} aria-pressed={active}
                        className={`w-full rounded-lg px-1 py-2 text-[11px] font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <button data-testid="chat-availability-continue" disabled={!state.availability.length}
        onClick={() => setState((s) => ({ ...s, phase: "review" }))}
        className="mt-5 w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold disabled:opacity-40">
        Review {state.availability.length} time{state.availability.length > 1 ? "s" : ""}
      </button>
    </div>
  );
}

function Review({ state, submitViewings, busy, back }) {
  const all = [...(state.results?.perfect || []), ...(state.results?.similar || [])];
  const selected = all.filter((l) => state.selected.includes(l.listing_id));
  return (
    <div data-testid="chat-review">
      <h3 className="font-heading font-semibold text-lg mb-3">Review your viewing request</h3>
      <ul className="space-y-2 text-sm">
        {selected.map((l) => (
          <li key={l.listing_id} className="flex justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
            <span className="font-medium">{l.address_street || l.neighborhood}{l.unit_number ? ` #${l.unit_number}` : ""}</span>
            <span className="text-muted-foreground shrink-0">{fmtRent(l.monthly_rent)}/mo · {bedsLabel(l)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-sm text-muted-foreground"><span className="font-medium text-foreground">{state.availability.length} time slots:</span>{" "}
        {state.availability.slice(0, 6).map((k) => { const [d, s] = k.split("|"); return `${d.slice(5)} ${s}`; }).join(" · ")}{state.availability.length > 6 ? "…" : ""}</p>
      <div className="mt-5 flex gap-2">
        <button data-testid="chat-review-back" onClick={back} className="rounded-full border border-border px-5 py-2.5 text-sm font-medium">Back</button>
        <button data-testid="chat-submit-viewings" onClick={submitViewings} disabled={busy}
          className="flex-1 rounded-full bg-accent text-accent-foreground py-2.5 font-semibold hover:bg-coral-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit viewing request
        </button>
      </div>
    </div>
  );
}

function AskTendly() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setAnswerText("");
    try {
      const res = await fetch(`${API}/chat/assist`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: q }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ") && line !== "data: [DONE]") {
            try { setAnswerText((t) => t + JSON.parse(line.slice(6)).t); } catch {}
          }
        }
      }
    } catch { setAnswerText("I'm here to help with your Vancouver home search — ask me anything about the process."); }
    setLoading(false);
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card shadow-sm">
      <button data-testid="ask-tendly-toggle" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center gap-2 px-5 py-3.5 text-sm font-semibold">
        <MessageCircleHeart className="w-4 h-4 text-accent" /> Questions along the way? Ask Tendly anything
      </button>
      {open && (
        <div className="px-5 pb-5">
          {answerText && <p data-testid="ask-tendly-answer" className="mb-3 rounded-xl bg-secondary px-4 py-3 text-sm leading-relaxed">{answerText}{loading && <span className="animate-pulse">▍</span>}</p>}
          <div className="flex gap-2">
            <input data-testid="ask-tendly-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="e.g. Is Kitsilano good for families?" aria-label="Ask Tendly a question"
              className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm" />
            <button data-testid="ask-tendly-send" onClick={ask} disabled={loading} aria-label="Send question"
              className="rounded-full bg-primary text-primary-foreground p-2.5 disabled:opacity-40"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
